import { environment } from "@environments/environment";

/** Resolve only API requests for the configured server. Never attach app credentials to assets or third parties. */
export function apiRequestUrl(requestUrl: string): URL | null {
  try {
    const base = new URL(
      environment.apiUrl,
      globalThis.location?.origin ?? "http://localhost",
    );
    const url = new URL(requestUrl, base);
    if (url.origin !== base.origin || url.username || url.password) {
      return null;
    }
    return /^\/(?:api(?:\/|$)|auth\/|users\/|public\/)/.test(url.pathname)
      ? url
      : null;
  } catch {
    return null;
  }
}

// Root aliases and the explicitly public router in routes/mod.rs. A path in
// this list is session-neutral only for its actual HTTP method: e.g. password
// reset does not exempt authenticated password changes or MFA management.
const PUBLIC_POST_PATHS = new Set([
  "/api/v1/auth/login",
  "/api/v1/auth/register",
  "/api/v1/auth/logout",
  "/api/v1/auth/refresh",
  "/api/v1/auth/reset-password",
  "/api/v1/auth/reset-password/confirm",
  "/api/v1/auth/verify-email",
  "/api/v1/auth/invitations/accept",
  "/api/v1/auth/send-verification-email",
  "/api/v1/auth/check-password-breach",
  "/api/v1/auth/mfa/security-key/login/verify",
  "/api/v1/users/login",
  "/api/v1/users/register",
  "/api/v1/public/auth/login",
  "/api/v1/public/auth/register",
  "/api/v1/public/auth/logout",
  "/api/v1/public/auth/refresh",
  "/api/v1/public/auth/reset-password",
  "/api/v1/public/auth/reset-password/confirm",
  "/api/v1/public/login",
  "/api/v1/public/register",
  "/api/v1/public/csp-report",
]);

const PUBLIC_GET_PATHS = new Set([
  "/api/v1/health",
  "/api/v1/ping",
  "/api/v1/auth/oauth/providers",
  "/api/v1/public/health",
  "/api/v1/public/symbols",
]);
const PUBLIC_MARKET_READ =
  /^\/api\/v1\/public\/(?:symbols\/[^/]+|tickers\/[^/]+|orderbook\/[^/]+(?:\/(?:depth|stats|metrics|history|volume-profile|heatmap|large-orders|imbalances))?|trades\/[^/]+(?:\/flow)?|candles\/[^/]+|avgPrice\/[^/]+|ticker\/bookTicker|microstructure\/[^/]+)$/;
const PUBLIC_PRICE_IMPACT =
  /^\/api\/v1\/public\/orderbook\/[^/]+\/price-impact$/;

function canonicalPath(pathname: string): string {
  // Retain the existing unversioned auth-client aliases, with the same exact
  // method/path policy; never match a suffix elsewhere in an API path.
  return /^\/(?:auth|users|public)\//.test(pathname)
    ? `/api/v1${pathname}`
    : pathname;
}

export function isSessionNeutralRequest(
  method: string,
  pathname: string,
): boolean {
  const path = canonicalPath(pathname);
  if (method === "POST") {
    return PUBLIC_POST_PATHS.has(path) || PUBLIC_PRICE_IMPACT.test(path);
  }
  return (
    (method === "GET" || method === "HEAD") &&
    (PUBLIC_GET_PATHS.has(path) || PUBLIC_MARKET_READ.test(path))
  );
}

export function isTenantNeutralRequest(
  method: string,
  pathname: string,
): boolean {
  return (
    isSessionNeutralRequest(method, pathname) ||
    (method === "GET" && pathname === "/api/v1/tenants/available") ||
    (method === "POST" && pathname === "/api/v1/tenants/switch")
  );
}

/** Public liveness checks are the only application-cache candidates. */
export function isPublicCacheRequest(
  method: string,
  requestUrl: string,
): boolean {
  const url = apiRequestUrl(requestUrl);
  return (
    method === "GET" &&
    url !== null &&
    (url.pathname === "/api/v1/health" || url.pathname === "/api/v1/ping")
  );
}
